import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import {
  COMMUNITY_VIEWS,
  communityCreateChallengePath,
  communityCreateDebatePath,
  isOfficialDailyDebate,
} from "../../utils/community";
import {
  useGetCommunityFeedQuery,
  useSearchCommunityQuery,
} from "../../store/api/communityApi";
import { useAnalytics } from "../../hooks/useAnalytics";
import { brandOutlinedButtonSx } from "../../utils/brandAssets";
import DebateCard from "./DebateCard";
import TodaysDebateSection from "./TodaysDebateHero";
import FeaturedChallengeCard from "./FeaturedChallengeCard";
import MatchupCard from "./MatchupCard";
import RosterCard from "./RosterCard";

const CommunityHome = ({ sport }) => {
  const [view, setView] = useState("trending");
  const [query, setQuery] = useState("");
  const searching = query.trim().length >= 2;
  const { data, isLoading, error } = useGetCommunityFeedQuery(sport, {
    skip: searching,
  });
  const search = useSearchCommunityQuery(
    { sport, q: query.trim() },
    { skip: !searching }
  );
  const { trackCustomEvent } = useAnalytics();
  const feed = searching ? search.data : data;

  React.useEffect(() => {
    if (feed) {
      trackCustomEvent("community_viewed", {
        sport,
        view: searching ? "search" : view,
      });
    }
  }, [feed, searching, sport, trackCustomEvent, view]);

  const matchups = feed?.matchups || [];
  const rosters = feed?.rosters || [];
  const challenges = feed?.challenges || [];
  const debates = (feed?.debates || []).filter(
    (debate) => !isOfficialDailyDebate(debate)
  );

  const heading = useMemo(() => {
    if (view === "challenges") return "Challenges";
    if (view === "battles") return "Roster battles";
    if (view === "debates") return "Debates";
    if (view === "rosters") return "Popular rosters";
    return "Trending";
  }, [view]);

  if ((isLoading && !searching) || (search.isLoading && searching)) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || search.error) {
    return (
      <Alert severity="error">Community is unavailable right now.</Alert>
    );
  }

  return (
    <Box>
      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: "0.16em" }}>
        Community
      </Typography>
      <Typography variant="h3" component="h1" fontWeight={800} sx={{ mt: 0.5 }}>
        Build it. Challenge it. Vote.
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 560 }}>
        Turn a sports take into a roster, a debate, or a challenge — backed by the
        historical record.
      </Typography>

      <TodaysDebateSection
        sport={sport}
        debate={searching ? data?.daily : feed?.daily}
        skipDailyFetch={searching}
      />

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2 }}>
        <Button
          component={RouterLink}
          to={communityCreateChallengePath(sport)}
          sx={brandOutlinedButtonSx}
        >
          Create challenge
        </Button>
        <Button
          component={RouterLink}
          to={communityCreateDebatePath(sport)}
          sx={brandOutlinedButtonSx}
        >
          Create debate
        </Button>
      </Box>

      <TextField
        fullWidth
        size="small"
        label="Search Community"
        placeholder="Rosters, battles, debates, challenges"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        sx={{ mt: 3 }}
      />

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2, mb: 3 }}>
        {COMMUNITY_VIEWS.map((item) => (
          <Chip
            key={item.id}
            label={item.label}
            clickable
            color={view === item.id ? "primary" : "default"}
            variant={view === item.id ? "filled" : "outlined"}
            onClick={() => setView(item.id)}
          />
        ))}
      </Box>

      {searching && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Results for “{query.trim()}”
        </Typography>
      )}

      {!searching &&
        (view === "trending" || view === "challenges") &&
        feed?.featuredChallenge && (
          <Box sx={{ mb: 3 }}>
            <FeaturedChallengeCard challenge={feed.featuredChallenge} sport={sport} />
          </Box>
        )}

      {(searching || view === "trending" || view === "challenges") &&
        challenges.length > 0 && (
          <Box sx={{ display: "grid", gap: 2, mb: 3 }}>
            {challenges
              .filter(
                (challenge) =>
                  searching || challenge.id !== feed?.featuredChallenge?.id
              )
              .map((challenge) => (
                <FeaturedChallengeCard
                  key={challenge.id}
                  challenge={challenge}
                  sport={sport}
                />
              ))}
          </Box>
        )}

      {(searching || view === "trending" || view === "debates") && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
            {view === "debates" || searching ? "Debates" : "Trending debates"}
          </Typography>
          {debates.length === 0 ? (
            <Typography color="text.secondary">
              No debates yet. Create one from Compare or Community.
            </Typography>
          ) : (
            <Box sx={{ display: "grid", gap: 2 }}>
              {debates.map((debate) => (
                <DebateCard key={debate.id} debate={debate} sport={sport} />
              ))}
            </Box>
          )}
        </Box>
      )}

      {(searching || view === "trending" || view === "battles") && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
            {view === "battles" ? heading : "Roster battles"}
          </Typography>
          {matchups.length === 0 ? (
            <Typography color="text.secondary">
              No battles yet. Challenge a published roster to start one.
            </Typography>
          ) : (
            <Box sx={{ display: "grid", gap: 2 }}>
              {matchups.map((matchup) => (
                <MatchupCard key={matchup.id} matchup={matchup} sport={sport} />
              ))}
            </Box>
          )}
        </Box>
      )}

      {(searching || view === "trending" || view === "rosters") && (
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
            {view === "rosters" ? heading : "Popular rosters"}
          </Typography>
          {rosters.length === 0 ? (
            <Typography color="text.secondary">
              No public rosters yet for this sport.
            </Typography>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
              }}
            >
              {rosters.map((roster) => (
                <RosterCard key={roster.id} roster={roster} sport={sport} />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CommunityHome;
